const coordinatePattern =
  /^(\d{1,3})°(\d{1,2}\.\d{1,3})'([NS])(\s+)(\d{1,3})°(\d{1,2}\.\d{1,3})'([EW])$/;
const coordinateDriftMinutes = 6;

class FooterTarget extends HTMLElement {
  connectedCallback() {
    this.mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.raf = null;
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.coordinatesEl = this.closest('.footer')?.querySelector('.footer-block__brand-coordinates') ?? null;
    this.baseCoordinates = this.coordinatesEl ? this.parseCoordinates(this.coordinatesEl.textContent) : null;

    this.handleMediaChange();
    this.mediaQuery.addEventListener('change', this.handleMediaChange);
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    this.unbind();
  }

  parseCoordinates(text) {
    const match = text.trim().match(coordinatePattern);
    if (!match) return null;
    const [, latDeg, latMin, latHem, gap, lonDeg, lonMin, lonHem] = match;
    return {
      latDegDigits: latDeg.length,
      latMinDigits: latMin.split('.')[1].length,
      latMin: parseFloat(latMin),
      latDeg,
      latHem,
      gap,
      lonDegDigits: lonDeg.length,
      lonMinDigits: lonMin.split('.')[1].length,
      lonMin: parseFloat(lonMin),
      lonDeg,
      lonHem,
    };
  }

  formatCoordinates(latMin, lonMin) {
    const c = this.baseCoordinates;
    const pad = (min, digits) => min.toFixed(digits).padStart(digits + 3, '0');
    return `${c.latDeg}°${pad(latMin, c.latMinDigits)}'${c.latHem}${c.gap}${c.lonDeg}°${pad(
      lonMin,
      c.lonMinDigits
    )}'${c.lonHem}`;
  }

  handleMediaChange() {
    if (this.mediaQuery.matches) {
      this.bind();
    } else {
      this.unbind();
    }
  }

  bind() {
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerleave', this.handlePointerLeave);
  }

  unbind() {
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.classList.remove('is-active');
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  handlePointerLeave() {
    this.classList.remove('is-active');
    if (this.coordinatesEl && this.baseCoordinates) {
      this.coordinatesEl.textContent = this.formatCoordinates(this.baseCoordinates.latMin, this.baseCoordinates.lonMin);
    }
  }

  handlePointerMove(event) {
    const rect = this.getBoundingClientRect();
    this.pointerX = event.clientX - rect.left;
    this.pointerY = event.clientY - rect.top;
    this.pointerRatioX = rect.width ? this.pointerX / rect.width : 0.5;
    this.pointerRatioY = rect.height ? this.pointerY / rect.height : 0.5;
    this.classList.add('is-active');

    if (!this.raf) {
      this.raf = requestAnimationFrame(() => this.render());
    }
  }

  render() {
    this.raf = null;
    this.style.setProperty('--footer-target-x', `${this.pointerX}px`);
    this.style.setProperty('--footer-target-y', `${this.pointerY}px`);

    if (this.coordinatesEl && this.baseCoordinates) {
      const c = this.baseCoordinates;
      const latMin = Math.min(100.000, Math.max(0, c.latMin + (0.5 - this.pointerRatioY) * 2 * coordinateDriftMinutes));
      const lonMin = Math.min(115.000, Math.max(0, c.lonMin + (this.pointerRatioX - 0.5) * 2 * coordinateDriftMinutes));
      this.coordinatesEl.textContent = this.formatCoordinates(latMin, lonMin);
    }
  }
}

if (!customElements.get('footer-target')) {
  customElements.define('footer-target', FooterTarget);
}
